<template>
    <div class="structured-table">
        <p class="field-title">
            {{ label }}
            <TooltipComponent v-if="item.description" :description="item.description" />
            <QuestionBadge :publishGateEnabled="publishGateEnabled" :required="!!required" />
        </p>
        <p v-if="item.description" class="structured-table-description">{{ item.description }}</p>

        <!-- ============================ STATIC TABLE ============================ -->
        <table v-if="schema.table_shape === 'static'" class="structured-table__table">
            <thead>
                <tr>
                    <th>{{ schema.row_label }}</th>
                    <th>{{ schema.value_label }}</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="row in schema.rows" :key="row.key">
                    <td class="structured-table__row-label">
                        <strong>{{ row.label }}</strong>
                        <span v-if="row.definition" class="structured-table__row-def">
                            — {{ row.definition }}
                        </span>
                    </td>
                    <td>
                        <Dropdown
                            :modelValue="staticAnswer[row.key]"
                            :options="schema.value_options"
                            optionLabel="label"
                            optionValue="label"
                            placeholder="Select..."
                            :disabled="disableEdit"
                            class="structured-table__dropdown"
                            @update:modelValue="setStaticCell(row.key, $event)"
                        />
                    </td>
                </tr>
            </tbody>
        </table>

        <!-- =========================== DYNAMIC TABLE ============================ -->
        <template v-else-if="schema.table_shape === 'dynamic'">
            <ul
                v-if="schema.pre_table_instructions && schema.pre_table_instructions.length"
                class="structured-table-instructions"
            >
                <li v-for="(instr, i) in schema.pre_table_instructions" :key="i">{{ instr }}</li>
            </ul>

            <table class="structured-table__table">
                <thead>
                    <tr>
                        <th v-for="col in schema.columns" :key="col.key">{{ col.label }}</th>
                        <th class="structured-table__delete-col"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="dynamicAnswer.length === 0">
                        <td
                            :colspan="schema.columns.length + 1"
                            class="structured-table__empty"
                        >
                            Use "Add row" to enter your first country.
                        </td>
                    </tr>
                    <tr v-for="(row, rowIdx) in dynamicAnswer" :key="rowIdx">
                        <td v-for="col in schema.columns" :key="col.key">
                            <!-- Country lookup against the reference country list, grouped by
                                 region (see countryTreeOptions below). TreeSelect's single-select
                                 v-model is a { [name]: true } keys object, not a plain string, so
                                 we convert to/from that shape inline against row[col.key]. -->
                            <TreeSelect
                                v-if="col.input === 'country_select'"
                                :modelValue="row[col.key] ? { [row[col.key]]: true } : {}"
                                :options="countryTreeOptions"
                                selectionMode="single"
                                panelClass="p-tree-single"
                                placeholder="Select country..."
                                :disabled="disableEdit"
                                class="structured-table__dropdown"
                                @update:modelValue="setDynamicCell(rowIdx, col.key, Object.keys($event || {})[0] || null)"
                            />
                            <!-- Integer headcount -->
                            <InputNumber
                                v-else-if="col.input === 'number'"
                                :modelValue="row[col.key]"
                                :min="col.min != null ? col.min : 0"
                                :useGrouping="false"
                                :disabled="disableEdit"
                                class="structured-table__number"
                                @update:modelValue="setDynamicCell(rowIdx, col.key, $event)"
                            />
                            <!-- Percentage range or simple categorical (same dropdown shape) -->
                            <Dropdown
                                v-else-if="col.input === 'percentage_range' || col.input === 'single_select'"
                                :modelValue="row[col.key]"
                                :options="col.options"
                                optionLabel="label"
                                optionValue="label"
                                placeholder="Select..."
                                :disabled="disableEdit"
                                class="structured-table__dropdown"
                                @update:modelValue="setDynamicCell(rowIdx, col.key, $event)"
                            />
                            <!-- Multi-select with search (languages) -->
                            <MultiSelect
                                v-else-if="col.input === 'multi_select_search'"
                                :modelValue="row[col.key] || []"
                                :options="languageOptions"
                                optionLabel="name"
                                optionValue="name"
                                placeholder="Select..."
                                :filter="true"
                                display="chip"
                                :disabled="disableEdit"
                                class="structured-table__dropdown"
                                @update:modelValue="setDynamicCell(rowIdx, col.key, $event)"
                            />
                        </td>
                        <td class="structured-table__delete-col">
                            <Button
                                icon="pi pi-trash"
                                class="p-button-text p-button-rounded p-button-sm"
                                :disabled="disableEdit || dynamicAnswer.length <= (schema.min_rows || 0)"
                                v-tooltip.left="
                                    dynamicAnswer.length <= (schema.min_rows || 0)
                                        ? `At least ${schema.min_rows || 1} row required`
                                        : 'Remove this row'
                                "
                                @click="removeDynamicRow(rowIdx)"
                            />
                        </td>
                    </tr>
                </tbody>
            </table>

            <div class="structured-table__add-row-bar">
                <Button
                    icon="pi pi-plus"
                    label="Add row"
                    class="p-button-outlined p-button-sm"
                    :disabled="disableEdit"
                    @click="addDynamicRow"
                />
            </div>

            <!-- Per-row error lines for dynamic tables. Country name is embedded so the
                 vendor knows which row is failing without re-counting positions. -->
            <p
                v-for="msg in dynamicRowErrorMessages"
                :key="msg"
                class="structured-table-validation structured-table-validation--bad"
            >
                {{ msg }}
            </p>
        </template>

        <!-- Static-table validation summary: appears once all rows are filled. -->
        <p
            v-if="schema.table_shape === 'static' && staticValidation.ready"
            class="structured-table-validation"
            :class="{
                'structured-table-validation--ok': staticValidation.ok,
                'structured-table-validation--bad': !staticValidation.ok,
            }"
        >
            {{ staticValidation.message }}
        </p>
    </div>
</template>

<script>
import { computed, onMounted, ref, watch } from 'vue';
import Dropdown from 'primevue/dropdown';
import MultiSelect from 'primevue/multiselect';
import InputNumber from 'primevue/inputnumber';
import Button from 'primevue/button';
import TreeSelect from 'primevue/treeselect';
import TooltipComponent from './TooltipComponent.vue';
import QuestionBadge from './QuestionBadge.vue';
import CountryService from '../../service/CountryService';
import { convertCountryDataToTreeSelect } from '../../service/common';

// Per-spec fallback language list. The dynamic-table schema declares
// `ref: 'ref_language'`, but we do not have a /languages endpoint in the
// frontend yet -- using this curated set as a safe default keeps the
// renderer functional. Swap to an API fetch when the endpoint lands.
const FALLBACK_LANGUAGES = Object.freeze([
    'English', 'Spanish', 'Portuguese', 'French', 'German', 'Italian',
    'Polish', 'Romanian', 'Dutch', 'Russian', 'Ukrainian',
    'Hindi', 'Tamil', 'Telugu', 'Bengali', 'Urdu',
    'Mandarin', 'Cantonese', 'Japanese', 'Korean',
    'Arabic', 'Hebrew', 'Turkish', 'Swahili',
].map((name) => ({ name })));

// Look up the numeric midpoint for a given range label in an options list.
// Returns 0 for unknown / missing labels -- treats "no selection" as 0,
// which is what the spec's validation math expects.
function midpointFor(label, options) {
    if (!label || !Array.isArray(options)) return 0;
    const opt = options.find((o) => o && o.label === label);
    return opt && Number.isFinite(opt.midpoint) ? opt.midpoint : 0;
}

// Evaluate the static-table midpoint-sum against the validation rule.
// Returns `{ ready, ok, message }`:
//   ready=false until every row has a selection (no premature ✗)
//   ok decides between the green-check copy and the red-cross copy
function evaluateStatic(answer, schema, rules) {
    const rows = Array.isArray(schema && schema.rows) ? schema.rows : [];
    const options = Array.isArray(schema && schema.value_options) ? schema.value_options : [];
    const allFilled = rows.every((r) => answer && typeof answer[r.key] === 'string' && answer[r.key] !== '');
    if (!allFilled) return { ready: false, ok: false, message: '' };

    const sum = rows.reduce((s, r) => s + midpointFor(answer[r.key], options), 0);
    const band = (rules && rules.tolerance_band) || { lower: 85, upper: 115 };
    const mode = (rules && rules.validation_mode) || 'exhaustive';

    if (sum > band.upper) {
        return {
            ready: true,
            ok: false,
            message:
                '✗ Selections appear to significantly exceed a complete distribution. Please review and adjust one or more rows before submitting.',
        };
    }
    if (mode === 'exhaustive' && sum < band.lower) {
        return {
            ready: true,
            ok: false,
            message:
                '✗ Selections appear to significantly fall short of a complete distribution. Please review and adjust one or more rows before submitting.',
        };
    }
    return {
        ready: true,
        ok: true,
        message: '✓ Selections appear to represent a reasonable distribution.',
    };
}

// For dynamic tables, decide whether a single row's work-model trio is balanced.
// Returns `{ ready, ok }` per row; if the trio isn't fully filled yet, ready=false.
function evaluateDynamicRow(row, schema, rules) {
    const exhaustiveCols = (rules && rules.exhaustive_columns) || [];
    if (exhaustiveCols.length === 0) return { ready: true, ok: true };

    const colDefs = Array.isArray(schema && schema.columns) ? schema.columns : [];
    const allFilled = exhaustiveCols.every((k) => row && typeof row[k] === 'string' && row[k] !== '');
    if (!allFilled) return { ready: false, ok: false };

    const sum = exhaustiveCols.reduce((s, k) => {
        const col = colDefs.find((c) => c && c.key === k);
        return s + midpointFor(row[k], col && col.options);
    }, 0);

    const band = (rules && rules.tolerance_band) || { lower: 85, upper: 115 };
    if (sum < band.lower || sum > band.upper) return { ready: true, ok: false };
    return { ready: true, ok: true };
}

export default {
    name: 'StructuredTableComponent',
    components: { Dropdown, MultiSelect, InputNumber, Button, TreeSelect, TooltipComponent, QuestionBadge },
    props: {
        item: { type: Object, required: true },
        label: { type: String, required: true },
        modelValue: { type: [Object, Array], default: null },
        validation_rules: { type: Object, default: () => ({}) },
        error_message: { type: String, default: '' },
        disableEdit: { type: Boolean, required: true },
        required: { type: Boolean, default: false },
        publishGateEnabled: { type: Boolean, default: false },
    },
    emits: ['update:modelValue', 'isValid'],
    setup(props, { emit }) {
        // The table schema lives in `item.dropdown_options` (the existing flat
        // dropdown field gets reused here as the schema-carrier so the answerbank
        // template endpoint did not need a new column on partner_field_info).
        const schema = computed(() => props.item.dropdown_options || {});

        // Working copies, normalized to the right shape per table_shape so the
        // template can always read / write through them without null guards.
        const staticAnswer = computed(() => {
            const v = props.modelValue;
            return v && typeof v === 'object' && !Array.isArray(v) ? v : {};
        });
        const dynamicAnswer = computed(() => {
            const v = props.modelValue;
            return Array.isArray(v) ? v : [];
        });

        // Reference data fetched once on mount. Falls back gracefully when the
        // endpoint is unavailable so the dropdown is never empty.
        const countryTreeOptions = ref([]);
        const languageOptions = ref(FALLBACK_LANGUAGES);

        onMounted(async () => {
            if (schema.value.table_shape !== 'dynamic') return;
            try {
                const cs = new CountryService();
                const list = await cs.getCountries();
                // CountryService returns the raw payload (`res.data`). The
                // production shape is a region-keyed object:
                //   { 'South Asia': [{name:'India'}, ...], 'North America': [...] }
                // convertCountryDataToTreeSelect turns this directly into
                // TreeSelect's { key, label, children } node shape, grouped by
                // region -- same helper ProfileAddress.vue already uses for its
                // single-select country TreeSelect.
                // Also accept flat-array and {data}/{countries} wrappers so the
                // renderer is resilient to schema drift -- wrap those into one
                // synthetic ungrouped region so TreeSelect always gets valid
                // grouped options.
                if (list && !Array.isArray(list) && typeof list === 'object'
                    && !Array.isArray(list.data) && !Array.isArray(list.countries)) {
                    countryTreeOptions.value = convertCountryDataToTreeSelect(list);
                    return;
                }
                let arr = [];
                if (Array.isArray(list)) {
                    arr = list;
                } else if (Array.isArray(list && list.data)) {
                    arr = list.data;
                } else if (Array.isArray(list && list.countries)) {
                    arr = list.countries;
                }
                const seen = new Set();
                const flatCountries = arr
                    .map((c) => {
                        if (typeof c === 'string') return { name: c };
                        if (c && typeof c === 'object') {
                            return { name: c.name || c.country_name || c.label || '' };
                        }
                        return null;
                    })
                    .filter((c) => {
                        if (!c || !c.name) return false;
                        if (seen.has(c.name)) return false;
                        seen.add(c.name);
                        return true;
                    })
                    .sort((a, b) => a.name.localeCompare(b.name));
                countryTreeOptions.value = flatCountries.length
                    ? [{
                        key: 'All Countries',
                        label: 'All Countries',
                        children: flatCountries.map((c) => ({ key: c.name, label: c.name })),
                    }]
                    : [];
            } catch (err) {
                console.warn('StructuredTable: country fetch failed; dropdown will be empty', err);
            }
        });

        // ---------------------------- Static handlers ----------------------------

        function setStaticCell(rowKey, value) {
            const next = { ...staticAnswer.value, [rowKey]: value };
            emit('update:modelValue', next);
        }

        const staticValidation = computed(() => {
            if (schema.value.table_shape !== 'static') return { ready: false, ok: false, message: '' };
            return evaluateStatic(staticAnswer.value, schema.value, props.validation_rules);
        });

        // --------------------------- Dynamic handlers ----------------------------

        function emptyRowFromSchema() {
            const out = {};
            for (const col of schema.value.columns || []) {
                if (col.input === 'multi_select_search') out[col.key] = [];
                else if (col.input === 'number') out[col.key] = null;
                else out[col.key] = '';
            }
            return out;
        }

        function setDynamicCell(rowIdx, colKey, value) {
            const next = dynamicAnswer.value.map((r, i) => (i === rowIdx ? { ...r, [colKey]: value } : r));
            emit('update:modelValue', next);
        }

        function addDynamicRow() {
            const next = [...dynamicAnswer.value, emptyRowFromSchema()];
            emit('update:modelValue', next);
        }

        function removeDynamicRow(rowIdx) {
            const next = dynamicAnswer.value.filter((_, i) => i !== rowIdx);
            emit('update:modelValue', next);
        }

        // Country-keyed error string list for the per-row work-model trio.
        // Spec copy: "✗ [Country] — In-office %, Hybrid %, and Remote % do not
        // sum to 100%. Please review and correct before submitting."
        const dynamicRowErrorMessages = computed(() => {
            if (schema.value.table_shape !== 'dynamic') return [];
            const msgs = [];
            dynamicAnswer.value.forEach((row) => {
                const verdict = evaluateDynamicRow(row, schema.value, props.validation_rules);
                if (verdict.ready && !verdict.ok) {
                    const country = (row && row.country) || 'This row';
                    msgs.push(
                        `✗ ${country} — In-office %, Hybrid %, and Remote % do not sum to 100%. Please review and correct before submitting.`
                    );
                }
            });
            return msgs;
        });

        // ------------------------- Overall validity emit -------------------------
        // A static table is valid once it is "ready" AND the midpoint sum passes.
        // A dynamic table is valid when:
        //   * at least min_rows rows are present
        //   * every required cell in every row has a non-empty value
        //   * every row's work-model trio is ready and ok
        // The result is emitted to the parent Wrapper via @isValid.
        //
        // COMPLETELY EMPTY state (no user input yet) is treated as
        // valid — an unanswered field is not the same as an invalid
        // one. Section confirmation + publish gate handle emptiness
        // separately. This lets vendors save incremental progress
        // without EWP blocking every save until fully populated.
        function emitValidity() {
            let ok = false;
            if (schema.value.table_shape === 'static') {
                // Empty static answer object → nothing entered yet → valid.
                const raw = staticAnswer.value;
                const isEmptyStatic = !raw
                    || (typeof raw === 'object' && Object.keys(raw).length === 0);
                if (isEmptyStatic) {
                    ok = true;
                } else {
                    ok = staticValidation.value.ready && staticValidation.value.ok;
                }
            } else if (schema.value.table_shape === 'dynamic') {
                const rows = dynamicAnswer.value;
                // Zero rows → user hasn't started → valid.
                if (!rows || rows.length === 0) {
                    ok = true;
                } else {
                    const minRows = schema.value.min_rows || 0;
                    const colDefs = schema.value.columns || [];
                    if (rows.length < minRows) {
                        ok = false;
                    } else {
                        ok = rows.every((row) => {
                            // Required-column check
                            for (const col of colDefs) {
                                if (!col.required) continue;
                                const v = row && row[col.key];
                                if (col.input === 'multi_select_search') {
                                    if (!Array.isArray(v) || v.length === 0) return false;
                                } else if (col.input === 'number') {
                                    if (v == null || v === '') return false;
                                } else if (typeof v !== 'string' || v === '') {
                                    return false;
                                }
                            }
                            // Work-model trio check
                            const verdict = evaluateDynamicRow(row, schema.value, props.validation_rules);
                            return verdict.ready && verdict.ok;
                        });
                    }
                }
            }
            emit('isValid', ok);
        }

        watch(
            [staticAnswer, dynamicAnswer, schema, () => props.validation_rules],
            () => emitValidity(),
            { immediate: true, deep: true }
        );

        return {
            schema,
            staticAnswer,
            dynamicAnswer,
            countryTreeOptions,
            languageOptions,
            setStaticCell,
            setDynamicCell,
            addDynamicRow,
            removeDynamicRow,
            staticValidation,
            dynamicRowErrorMessages,
        };
    },
};
</script>

<style lang="scss" scoped>
.structured-table {
    width: 100%;
    margin-bottom: 1.5rem;
}

.structured-table-description {
    color: var(--text-color-secondary, #495057);
    font-size: 0.95rem;
    margin: 0.25rem 0 0.75rem 0;
}

.structured-table__table {
    width: 100%;
    border-collapse: collapse;
    margin: 0.75rem 0 0.5rem 0;

    th,
    td {
        border: 1px solid #e9ecef;
        padding: 0.75rem;
        text-align: left;
        vertical-align: middle;
    }

    thead th {
        background-color: #f8f9fa;
        font-weight: 600;
        font-size: 0.9rem;
        color: #495057;
    }

    tbody tr:nth-child(even) td {
        background-color: #fbfbfc;
    }
}

.structured-table__row-label {
    width: 60%;
}
.structured-table__row-def {
    color: #6c757d;
    font-weight: 400;
}

.structured-table__dropdown {
    width: 100%;
}
.structured-table__number {
    width: 100%;
    /* PrimeVue InputNumber renders nested -- target the inner input so
       the width attaches to the actual control */
    :deep(.p-inputtext) {
        width: 100%;
    }
}

.structured-table__delete-col {
    width: 3rem;
    text-align: center;
}

.structured-table__empty {
    text-align: center;
    color: #6c757d;
    font-style: italic;
    padding: 1.5rem !important;
}

.structured-table__add-row-bar {
    margin-top: 0.5rem;
}

.structured-table-instructions {
    margin: 0.5rem 0 0.75rem 1.25rem;
    color: #495057;
    font-size: 0.9rem;
    li {
        margin-bottom: 0.35rem;
    }
}

.structured-table-validation {
    margin: 0.75rem 0 0 0;
    font-size: 0.9rem;
    font-weight: 500;
}
.structured-table-validation--ok {
    color: #166534; /* green */
}
.structured-table-validation--bad {
    color: #b91c1c; /* red */
}
</style>
